/**
 * PropertyCard Component
 * Displays a single property in a card format for the property grid
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import type { Property } from "../../types";
import Icon from "../AppIcon";
import Button from "../ui/Button";

interface PropertyCardProps {
	property: Property;
}

const PropertyCard: React.FC<PropertyCardProps> = ({ property }) => {
	const navigate = useNavigate();

	// Format price with Nigerian Naira symbol
	const formatPrice = (price: number | undefined, period?: string) => {
		if (!price) return "Price on request";
		return `₦ ${price.toLocaleString()} ${period ? `/ ${period}` : ""}`;
	};

	// Get rent period label
	const getRentPeriodLabel = (rentType?: string) => {
		switch (rentType) {
			case "annual":
				return "per year";
			case "biannual":
				return "per 6 months";
			case "monthly":
				return "per month";
			case "weekly":
				return "per week";
			default:
				return "per year";
		}
	};

	// Get status badge color
	const getStatusColor = (status: string) => {
		switch (status.toLowerCase()) {
			case "available":
				return "bg-green-600 text-white border-green-700";
			case "pending":
				return "bg-blue-600 text-white border-blue-700";
			case "leased":
				return "bg-purple-600 text-white border-purple-700";
			case "sold":
				return "bg-red-600 text-white border-red-700";
			case "maintenance":
				return "bg-yellow-600 text-white border-yellow-700";
			default:
				return "bg-gray-800 text-white border-gray-900";
		}
	};

	// Get status label
	const getStatusLabel = (status: string) => {
		const labels: Record<string, string> = {
			available: "Available",
			pending: "Pending Deal",
			leased: "Leased",
			sold: "Sold",
			maintenance: "Maintenance",
		};
		return labels[status.toLowerCase()] || status;
	};

	// Determine if property is for lease or sale based on Admin listing
	// Check both rent_amount (new) and monthly_rent (legacy)
	const hasRentAmount =
		(property.rent_amount && property.rent_amount > 0) ||
		(property.monthly_rent && property.monthly_rent > 0);
	const isForLease = hasRentAmount;
	const isForSale = property.purchase_price && property.purchase_price > 0;

	// Get the main image or placeholder
	const mainImage =
		property.images && property.images.length > 0 ? property.images[0] : null;

	return (
		<div className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg smooth-transition group">
			{/* Property Image */}
			<div className="relative aspect-video overflow-hidden bg-muted">
				{mainImage ? (
					<img
						src={mainImage}
						alt={property.name}
						className="w-full h-full object-cover group-hover:scale-105 smooth-transition"
					/>
				) : (
					<div className="w-full h-full flex items-center justify-center">
						<Icon
							name="Building"
							size={64}
							className="text-muted-foreground opacity-30"
						/>
					</div>
				)}

				{/* Status Badge */}
				<div className="absolute top-3 left-3 z-[1]">
					<span
						className={`px-3 py-1.5 rounded-full text-xs font-semibold border shadow-lg backdrop-blur-md ${getStatusColor(
							property.status,
						)}`}
					>
						{getStatusLabel(property.status)}
					</span>
				</div>

				{/* Property Type Badge */}
				{property.property_type && (
					<div className="absolute top-3 right-3 z-[1]">
						<span className="px-3 py-1.5 rounded-full text-xs font-semibold bg-black/80 text-white border border-white/20 shadow-lg backdrop-blur-md">
							{property.property_type.charAt(0).toUpperCase() +
								property.property_type.slice(1)}
						</span>
					</div>
				)}

				{/* Dual-Purpose Badge - NEW */}
				{isForLease && isForSale && (
					<div className="absolute bottom-3 left-3 z-[1]">
						<span className="px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-blue-600 to-green-600 text-white border border-white/20 shadow-lg backdrop-blur-md flex items-center gap-1.5">
							<Icon name="Tag" size={12} />
							Lease & Sale Available
						</span>
					</div>
				)}
			</div>

			{/* Property Details */}
			<div className="p-5">
				{/* Title */}
				<h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-1 group-hover:text-primary smooth-transition">
					{property.name}
				</h3>

				{/* Location */}
				<div className="flex flex-col gap-1 mb-3">
					{/* Unit Number - if exists */}
					{property.unit_number && (
						<div className="flex items-center text-sm text-muted-foreground">
							<Icon name="Hash" size={16} className="mr-1" />
							<span className="font-medium">{property.unit_number}</span>
						</div>
					)}

					{/* City, State, Country */}
					<div className="flex items-center text-sm text-muted-foreground">
						<Icon name="MapPin" size={16} className="mr-1" />
						<span className="line-clamp-1">
							{property.city}, {property.state}, {property.country}
						</span>
					</div>
				</div>

				{/* Price */}
				<div className="mb-4">
					{isForLease && isForSale ? (
						// Dual pricing - side by side layout with clear labels
						<div className="grid grid-cols-2 gap-3 p-3 bg-muted/40 rounded-lg border border-border">
							<div className="flex flex-col">
								<div className="flex items-center gap-1.5 mb-1.5">
									<Icon name="Key" size={14} className="text-blue-600" />
									<span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">
										Lease
									</span>
								</div>
								<p className="text-base font-bold text-foreground">
									{property.rent_amount
										? formatPrice(
												property.rent_amount,
												getRentPeriodLabel(property.rent_type),
										  )
										: formatPrice(property.monthly_rent, "month")}
								</p>
							</div>
							<div className="flex flex-col border-l border-border pl-3">
								<div className="flex items-center gap-1.5 mb-1.5">
									<Icon name="Tag" size={14} className="text-green-600" />
									<span className="text-xs font-semibold text-green-600 uppercase tracking-wide">
										Sale
									</span>
								</div>
								<p className="text-base font-bold text-foreground">
									{formatPrice(property.purchase_price)}
								</p>
							</div>
						</div>
					) : isForLease ? (
						// Only lease available
						<p className="text-2xl font-bold text-primary">
							{property.rent_amount
								? formatPrice(
										property.rent_amount,
										getRentPeriodLabel(property.rent_type),
								  )
								: formatPrice(property.monthly_rent, "month")}
						</p>
					) : isForSale ? (
						// Only sale available
						<p className="text-2xl font-bold text-primary">
							{formatPrice(property.purchase_price)}
						</p>
					) : (
						// No pricing available
						<p className="text-2xl font-bold text-muted-foreground">
							Price on request
						</p>
					)}
				</div>

				{/* Key Facts */}
				<div className="flex items-center space-x-4 mb-5 pb-5 border-b border-border">
					{property.bedrooms > 0 && (
						<div className="flex items-center text-sm text-muted-foreground">
							<Icon name="Bed" size={16} className="mr-1" />
							<span>{property.bedrooms} Beds</span>
						</div>
					)}

					{property.bathrooms > 0 && (
						<div className="flex items-center text-sm text-muted-foreground">
							<Icon name="Bath" size={16} className="mr-1" />
							<span>{property.bathrooms} Baths</span>
						</div>
					)}

					{property.square_feet > 0 && (
						<div className="flex items-center text-sm text-muted-foreground">
							<Icon name="Maximize" size={16} className="mr-1" />
							<span>{property.square_feet} sqft</span>
						</div>
					)}
				</div>

				{/* Action Buttons */}
				<div className="flex flex-col sm:flex-row gap-2">
					{/* S1: View Details Button */}
					<Button
						variant="outline"
						className="flex-1 w-full sm:w-auto min-w-0"
						onClick={() => navigate(`/properties/${property.id}`)}
					>
						<span className="truncate">Details</span>
					</Button>

					{/* S2.1: Request to Lease (if listed under lease by Admin) */}
					{isForLease && (
						<Button
							className="flex-1 w-full sm:w-auto min-w-0"
							onClick={() => {
								navigate(`/properties/${property.id}?action=request-lease`);
							}}
						>
							<span className="truncate">Rent</span>
						</Button>
					)}

					{/* S2.2: Request to Buy (if listed under Sale by Admin) */}
					{isForSale && (
						<Button
							className="flex-1 w-full sm:w-auto min-w-0"
							onClick={() => {
								navigate(`/properties/${property.id}?action=request-buy`);
							}}
						>
							<span className="truncate">Buy</span>
						</Button>
					)}
				</div>
			</div>
		</div>
	);
};

export default PropertyCard;
